import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Form, Input, InputNumber, Modal, Space, Table, Typography, message, DatePicker } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchExchangeRates,
  createExchangeRate,
  updateExchangeRate,
  deleteExchangeRate,
  dispatchExchangeRatesUpdated,
  EXCHANGE_RATES_UPDATED_EVENT,
} from "../../../shared/lib/exchangeRates";
import { readCachedSystemConfig, fetchSystemConfig } from "../../../shared/lib/systemConfig";
import { toApiNumber } from "../../../utils/formatters";
import useTablePagination from "../../../shared/ui/useTablePagination";

const ExchangeRates = () => {
  const { t } = useTranslation();
  const { pagination } = useTablePagination();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());

  const mainCode = systemConfig?.currency_code || "IQD";
  const secondaryCode = systemConfig?.secondary_currency_code || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await fetchSystemConfig();
      setSystemConfig(cfg);
      const list = await fetchExchangeRates();
      setRows(list);
    } catch (e) {
      message.error(e?.response?.data?.message || t("exchangeRatesLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onRefresh);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onRefresh);
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      effective_from: dayjs(),
      main_per_secondary: undefined,
      note: "",
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      effective_from: record.effective_from ? dayjs(record.effective_from) : dayjs(),
      main_per_secondary: toApiNumber(record.main_per_secondary, 0),
      note: record.note || "",
    });
    setModalOpen(true);
  };

  const handleDelete = async (record) => {
    Modal.confirm({
      title: t("exchangeRatesDeleteConfirm"),
      onOk: async () => {
        try {
          await deleteExchangeRate(record.id);
          message.success(t("exchangeRatesDeleted"));
          dispatchExchangeRatesUpdated();
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || t("exchangeRatesDeleteFailed"));
        }
      },
    });
  };

  const columns = useMemo(() => {
    const dash = t("exchangeRatesDash");
    return [
      {
        title: t("exchangeRatesEffectiveFrom"),
        dataIndex: "effective_from",
        key: "effective_from",
        render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : dash),
      },
      {
        title: t("exchangeRatesColumnRate", { main: mainCode, secondary: secondaryCode || "?" }),
        dataIndex: "main_per_secondary",
        key: "main_per_secondary",
        render: (v) => (v !== undefined && v !== null ? Number(v).toLocaleString() : dash),
      },
      {
        title: `100 ${mainCode || "?"} = ? ${secondaryCode}`,
        key: "per_100",
        render: (_, record) => {
          const v = toApiNumber(record?.main_per_secondary, 0);
          if (!v) return dash;
          return `${(v * 100).toLocaleString()} ${mainCode}`;
        },
      },
      {
        title: t("exchangeRatesColumnInverse"),
        key: "inv",
        render: (_, record) => {
          const m = toApiNumber(record?.main_per_secondary, 0);
          if (!m) return dash;
          const inv = 1 / m;
          return t("exchangeRatesInverseApprox", {
            secondary: secondaryCode || "?",
            inv: inv.toLocaleString(undefined, { maximumFractionDigits: 6 }),
            main: mainCode,
          });
        },
      },
      { title: t("exchangeRatesColumnNote"), dataIndex: "note", key: "note", ellipsis: true },
      {
        title: t("exchangeRatesColumnActions"),
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>
              {t("edit")}
            </Button>
            <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
              {t("delete")}
            </Button>
          </Space>
        ),
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCode, secondaryCode, t]);

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        effective_from: values.effective_from.format("YYYY-MM-DD"),
        main_per_secondary: values.main_per_secondary,
        note: values.note || null,
      };
      if (editing) {
        await updateExchangeRate(editing.id, payload);
        message.success(t("exchangeRatesUpdated"));
      } else {
        await createExchangeRate(payload);
        message.success(t("exchangeRatesAdded"));
      }
      setModalOpen(false);
      dispatchExchangeRatesUpdated();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || t("exchangeRatesSaveFailed"));
    }
  };

  return (
    <div className="list-page">
      <Card
        title={<Typography.Title level={4} style={{ margin: 0 }}>{t("exchangeRatesPageTitle")}</Typography.Title>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!secondaryCode}>
            {t("exchangeRatesAddRate")}
          </Button>
        }
        loading={loading}
        variant="borderless"
      >
        {!secondaryCode ? (
          <Typography.Text type="warning">
            {t("exchangeRatesNoSecondaryWarning", { main: mainCode })}
          </Typography.Text>
        ) : (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            {t("exchangeRatesHelp", { main: mainCode, secondary: secondaryCode })}
          </Typography.Paragraph>
        )}

        <Table rowKey="id" dataSource={rows} columns={columns} pagination={pagination} />

        <Modal
          title={editing ? t("exchangeRatesModalEdit") : t("exchangeRatesModalAdd")}
          open={modalOpen}
          onOk={handleModalOk}
          onCancel={() => setModalOpen(false)}
          destroyOnHidden
        >
          <Form form={form} layout="vertical">
            <Form.Item name="effective_from" label={t("exchangeRatesEffectiveFrom")} rules={[{ required: true, message: t("exchangeRatesEffectiveFromRequired", "Effective date is required") }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="main_per_secondary"
              label={t("exchangeRatesHowManyLabel", { secondary: secondaryCode || "?", main: mainCode })}
              rules={[{ required: true, message: t("exchangeRatesRateRequired", "Please enter a rate.") }]}
            >
              <InputNumber min={0.000001} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="note" label={t("exchangeRatesNote")}>
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default ExchangeRates;
